/**
 * Build ZIP layout aligned with POST /Crash/import-zip (manifest + crashes/*.json).
 * @see fred-api/docs/import-pacchetti-zip-incidenti-json.md
 */

import { normalizeCrashPayloadDates } from '../utils/crash-payload-dates'
import {
  compressCrashImageDataUrl,
  shouldCompressImageDataUrl,
} from '../utils/crash-image-compress'

const CLIENT_APP_PREFIX = 'fred-pwa'

/**
 * Version string for manifest `client_app_version` (e.g. fred-pwa/1.0.0).
 */
export function getClientAppVersion() {
  const v =
    typeof process !== 'undefined' && process.env?.REACT_APP_VERSION
      ? String(process.env.REACT_APP_VERSION)
      : '1.0.0'
  return `${CLIENT_APP_PREFIX}/${v}`
}

/**
 * @param {Array<{ payload: object }>} rows
 * @returns {{ ok: true, organizationId: number } | { ok: false, code: 'ORG_MISSING' | 'ORG_MIXED' }}
 */
export function resolveSingleSourceOrganization(rows) {
  const orgs = rows.map((r) => {
    const o = r.payload?.crash?.organization
    if (o === undefined || o === null || o === '') return null
    const n = Number(o)
    return Number.isFinite(n) ? n : null
  })
  if (orgs.some((o) => o === null)) {
    return { ok: false, code: 'ORG_MISSING' }
  }
  const first = orgs[0]
  if (!orgs.every((o) => o === first)) {
    return { ok: false, code: 'ORG_MIXED' }
  }
  return { ok: true, organizationId: first }
}

/**
 * @param {Object} params
 * @param {number} params.sourceOrganizationId
 * @param {number} params.createdByUserId
 * @param {string} params.packageId - UUID
 * @param {string} params.exportedAt - ISO 8601
 * @returns {Object}
 */
export function buildManifestJson({
  sourceOrganizationId,
  createdByUserId,
  packageId,
  exportedAt
}) {
  return {
    schema_version: '1.0',
    exported_at: exportedAt,
    source_organization_id: sourceOrganizationId,
    created_by_user_id: createdByUserId,
    client_app_version: getClientAppVersion(),
    package_id: packageId
  }
}

/**
 * Safe entry name under crashes/ (letters, digits, _, ., -).
 * @param {number} index
 * @param {string} clientUuid
 */
export function sanitizeCrashEntryFilename(index, clientUuid) {
  const safe = String(clientUuid || `row${index}`)
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .slice(0, 48)
  return `crash_${index}_${safe}.json`
}

/**
 * Stem used for attachment folder names (filename without .json).
 * @param {string} crashJsonFilename
 */
export function getCrashEntryStem(crashJsonFilename) {
  return String(crashJsonFilename).replace(/\.json$/i, '')
}

const IMAGE_DATA_URL_RE = /^data:(image\/[a-zA-Z0-9.+-]+);base64,(.+)$/s

const MIME_TO_EXTENSION = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
}

/**
 * @param {string} dataUrl
 * @returns {{ mime: string, ext: string, bytes: Uint8Array }}
 */
export function parseImageDataUrl(dataUrl) {
  const match = String(dataUrl).match(IMAGE_DATA_URL_RE)
  if (!match) {
    throw new Error('Invalid image data URL')
  }
  const mime = match[1].toLowerCase()
  const ext = MIME_TO_EXTENSION[mime]
  if (!ext) {
    throw new Error(`Unsupported image MIME type: ${mime}`)
  }
  const binary = atob(match[2])
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return { mime, ext, bytes }
}

/**
 * Externalize crash.images base64 data URLs to attachments/{entryStem}/NNN.ext.
 * Legacy inline data URLs are left unchanged when externalization is skipped (no images).
 *
 * @param {object} payload
 * @param {Record<string, unknown>} [formModel]
 * @param {string} crashEntryStem - e.g. crash_0_clientUuid (no .json)
 * @returns {Promise<{ jsonPayload: object, attachmentFiles: Array<{ zipPath: string, bytes: Uint8Array }> }>}
 */
export async function prepareCrashPayloadForZipExport(
  payload,
  formModel,
  crashEntryStem,
) {
  const jsonPayload = serializePayloadForZipFile(payload, formModel)
  const images = jsonPayload?.crash?.images
  if (!Array.isArray(images) || images.length === 0) {
    return { jsonPayload, attachmentFiles: [] }
  }

  const attachmentFiles = []
  const imageRefs = []
  let attachmentIndex = 0

  for (const imageValue of images) {
    if (typeof imageValue !== 'string' || imageValue.length === 0) {
      continue
    }
    if (imageValue.startsWith('attachments/')) {
      imageRefs.push(imageValue)
      continue
    }
    if (!imageValue.startsWith('data:image/')) {
      imageRefs.push(imageValue)
      continue
    }

    let dataUrl = imageValue
    if (shouldCompressImageDataUrl(dataUrl)) {
      dataUrl = await compressCrashImageDataUrl(dataUrl)
    }

    const { ext, bytes } = parseImageDataUrl(dataUrl)
    attachmentIndex += 1
    const fileName = `${String(attachmentIndex).padStart(3, '0')}.${ext}`
    const zipPath = `attachments/${crashEntryStem}/${fileName}`
    attachmentFiles.push({ zipPath, bytes })
    imageRefs.push(zipPath)
  }

  jsonPayload.crash.images = imageRefs
  return { jsonPayload, attachmentFiles }
}

/**
 * Clone payload for ZIP: normalize dates then JSON-safe clone.
 * @param {object} payload
 * @param {Record<string, unknown>} [formModel]
 */
export function serializePayloadForZipFile(payload, formModel = null) {
  const normalized = formModel
    ? normalizeCrashPayloadDates(payload, formModel)
    : payload
  return JSON.parse(JSON.stringify(normalized))
}
